import { Observable, observe, Scope } from 'src/3rds/cube'
import type { Atalho } from './Dashboards.periodo'
import type { DefinicaoPerspectiva, Perspectiva } from './Dashboards.perspectivas'

// :: Types (espelham apps/server/src/dashboard/operacao.types.ts)

export type CanalAgora = {
    canal: string
    pendentes: number
    emRetentativa: number
    processando: number
    presas: number
    maisAntigaSegundos: number | null
}

export type CanalTotais = {
    canal: string
    enviadas: number
    dlq: number
    falhas: number
    erros400: number
}

export type DiaCanal = CanalTotais & {
    dia: string
    esperaP50Ms: number | null
    esperaP95Ms: number | null
}

export type ErroAgrupado = {
    http: number | null
    codigoMeta: string | null
    qtde: number
    exemplo: string
}

export type Operacao = {
    periodo: { de: string; ate: string }
    geradoEm: string
    agora: CanalAgora[]
    erros400DesdeOInicio: number
    totais: CanalTotais[]
    serie: DiaCanal[]
    erros: ErroAgrupado[]
    detalheDesde: string
}

export type MensagemDlq = {
    id: number
    canal: string
    criadaEm: string
    ultimaTentativa: string
    tentativas: number
    erro: string
    destino?: string
    parceiro?: string
    dealId?: string
}

export const DLQ_POR_PAGINA = 10

// :: Negócios e disputas (espelham apps/server/src/dashboard/negocios.types.ts)

export type Origem = 'todos' | 'lia'

export type PorFase = { A: number; R: number; K: number; C: number }

export type Negocios = {
    periodo: { de: string; ate: string }
    origem: Origem
    geradoEm: string
    carteira: PorFase
    pipelines: (PorFase & { pipeline: string; nome: string })[]
    esteiras: (PorFase & { esteira: string })[]
    prazos: { vencem7: number; vencem15: number; vencido: number; semPrazo: number }
    gatilhos: { gatilho: string; qtde: number }[]
    criados: PorFase & { L: number; total: number }
    movimento: { dia: string; abordagens: number; reservas: number; clientes: number; derrubadas: number }[]
    derrubadas: {
        total: number
        porReservaDocumento: number
        porReservaEtapa: number
        porCota: number
        porCliente: number
    }
    historicoDesde: string | null
}

export type ReservaVencendo = {
    dealId: string
    negocio: string
    parceiro?: string
    pipeline: string
    fase: 'R' | 'K'
    gatilho?: string
    venceEm: string
}

export const RESERVAS_POR_PAGINA = 10

// :: Parceiros (espelham apps/server/src/dashboard/parceiros.types.ts)

export type Parceiros = {
    periodo: { de: string; ate: string }
    geradoEm: string
    cadastrados: number
    ativos: number
    consultas: number
    cadastros: number
    renovacoes: number
    parados: number
    nuncaConsultaram: number
    faixas: { faixa: string; parceiros: number; consultas: number }[]
    fatiaTop10: number
    porDia: { dia: string; parceiros: number; consultas: number }[]
}

export type OrdemParceiros = 'consultas' | 'cadastros' | 'taxa' | 'ultima' | 'carteira' | 'nome'

export type SituacaoParceiro = 'todos' | 'ativos' | 'parados' | 'nunca'

export type Parceiro = {
    ownerId: string
    nome: string
    consultas: number
    cadastros: number
    renovacoes: number
    taxa: number | null
    madrugada: number | null
    ultimaConsulta?: string
    abordagem: number
    reserva: number
    cliente: number
}

export const PARCEIROS_POR_PAGINA = 15

export const ROTULO_SITUACAO: Record<SituacaoParceiro, string> = {
    todos: 'Todos os parceiros',
    ativos: 'Consultaram no período',
    parados: 'Parados há mais de 30 dias',
    nunca: 'Nunca consultaram'
}

// :: Consultas de lead (espelham apps/server/src/dashboard/consultas.types.ts)

/** No filtro de desfecho, junta todos os "Aguardar N dias" numa opção só. */
export const AGUARDAR_COM_PRAZO = 'Aguardar N dias'

export type FiltrosConsultas = {
    classificacoes: string[]
    ufs: string[]
    /** "UF|MUNICÍPIO". */
    municipios: string[]
    secoes: string[]
    divisoes: string[]
    naturezas: string[]
    /** Código do CNAE ou parte da descrição ou do grupo. */
    cnae: string
    /** owner_id de quem consultou. */
    parceiros: string[]
    /** owner_id: o parceiro e toda a rede abaixo dele. */
    rede: string
    cnpjs: string[]
    negocios: string[]
}

export const FILTROS_CONSULTAS_VAZIOS: FiltrosConsultas = {
    classificacoes: [],
    ufs: [],
    municipios: [],
    secoes: [],
    divisoes: [],
    naturezas: [],
    cnae: '',
    parceiros: [],
    rede: '',
    cnpjs: [],
    negocios: []
}

export type Metricas = { consultas: number; cadastros: number; renovacoes: number; investido: number }

export type NoParceiro = Metricas & { ownerId: string; nome: string; temFilhos: boolean }

export type FilhosParceiro = { caminho: string[]; proprias?: Metricas; filhos: NoParceiro[] }

export type PontoMapa = {
    uf: string
    municipio: string
    latitude: number
    longitude: number
    consultas: number
    cnpjs: number
}

export type Consultas = Metricas & {
    periodo: { de: string; ate: string }
    geradoEm: string
    valorPorConsulta: number
    cnpjs: number
    granularidade: 'dia' | 'mes'
    serie: { chave: string; consultas: number }[]
    mapa: PontoMapa[]
    maisConsultadas: { cnpj: string; nome: string; consultas: number; cadastros: number }[]
    cnae: { divisao: string; descricao: string; consultas: number; cadastros: number }[]
    parceiros: NoParceiro[]
}

export type Consulta = {
    id: string
    quando: string
    ownerId: string
    parceiro: string
    telefone: string
    cnpj: string
    razaoSocial: string
    nomeFantasia: string
    matrizFilial: string
    coligadas: number
    funcionarios: number | null
    funcionariosColigadas: number | null
    classificacao: string
    dealId?: string
    nomeNegocio?: string
    negocioCriadoEm?: string
    parceiroNegocio?: string
    renovacoesNegocio?: number
    porte: string
    cnae: string
    descricaoCnae: string
    grupoCnae: string
    secaoCnae: string
    faturamento: number | null
    uf: string
    municipio: string
    idadeEmpresa: number | null
    naturezaJuridica: string
    optanteSimples: string
    situacaoCadastral: string
    mensagem: string
}

export type OpcoesConsultas = {
    classificacoes: { valor: string; consultas: number }[]
    ufs: string[]
    municipios: { uf: string; municipio: string }[]
    secoes: string[]
    divisoes: { secao: string; divisao: string }[]
    naturezas: string[]
}

export type ParceiroEncontrado = { ownerId: string; nome: string; email: string }

export const CONSULTAS_POR_PAGINA = 25

/** Chave de um nó da árvore de parceiros: o caminho de owner_id desde o topo da rede. */
export const chaveDoCaminho = (caminho: string[]) => caminho.join('/')

// :: Scopes

@Observable
export class DashboardsScope extends Scope {
    @observe() perspectivas: DefinicaoPerspectiva[] = []
    @observe() aba: Perspectiva = 'operacao'

    @observe() atalho: Atalho = '30'
    @observe() de = ''
    @observe() ate = ''
    @observe() erroPeriodo?: string

    @observe() carregando = false
    @observe() operacao?: Operacao

    @observe() dlqCanal = ''
    @observe() dlqPagina = 0
    @observe() dlqTotal = 0
    @observe() dlq: MensagemDlq[] = []
    @observe() carregandoDlq = false

    @observe() gerandoPdf = false
    onBaixarPdf = Scope.ASYNC_ACTION

    onAbaChange = Scope.ASYNC_ACTION_ONE<Perspectiva>()
    onAtalhoChange = Scope.ASYNC_ACTION_ONE<Atalho>()
    onDeChange = Scope.ASYNC_ACTION_STRING
    onAteChange = Scope.ASYNC_ACTION_STRING
    onAtualizar = Scope.ASYNC_ACTION

    @observe() origem: Origem = 'todos'
    @observe() negocios?: Negocios
    @observe() vencendoDias: 7 | 15 = 15
    @observe() vencendoPagina = 0
    @observe() vencendoTotal = 0
    @observe() vencendo: ReservaVencendo[] = []
    @observe() carregandoVencendo = false

    @observe() parceiros?: Parceiros
    @observe() buscaParceiro = ''
    @observe() situacaoParceiro: SituacaoParceiro = 'todos'
    @observe() ordemParceiros: OrdemParceiros = 'consultas'
    @observe() rankingPagina = 0
    @observe() rankingTotal = 0
    @observe() ranking: Parceiro[] = []
    @observe() carregandoRanking = false

    onBuscaParceiroChange = Scope.ASYNC_ACTION_STRING
    onSituacaoParceiroChange = Scope.ASYNC_ACTION_ONE<SituacaoParceiro>()
    onOrdemParceirosChange = Scope.ASYNC_ACTION_ONE<OrdemParceiros>()
    onRankingPaginaChange = Scope.ASYNC_ACTION_ONE<number>()

    onOrigemChange = Scope.ASYNC_ACTION_ONE<Origem>()
    onVencendoDiasChange = Scope.ASYNC_ACTION_ONE<7 | 15>()
    onVencendoPaginaChange = Scope.ASYNC_ACTION_ONE<number>()

    onDlqCanalChange = Scope.ASYNC_ACTION_STRING
    onDlqPaginaChange = Scope.ASYNC_ACTION_ONE<number>()

    // :: Consultas de lead

    @observe() consultas?: Consultas
    @observe() filtrosConsultas: FiltrosConsultas = FILTROS_CONSULTAS_VAZIOS
    /** Nomes dos parceiros usados nos filtros (owner_id → nome), para as etiquetas. */
    @observe() nomesParceiros: Record<string, string> = {}
    @observe() opcoesConsultas?: OpcoesConsultas
    @observe() sugestoesParceiro: ParceiroEncontrado[] = []

    /** Nós abertos da árvore de parceiros (chaveDoCaminho) e os filhos já carregados de cada um. */
    @observe() parceirosAbertos: string[] = []
    @observe() filhosParceiros: Record<string, FilhosParceiro> = {}
    @observe() carregandoParceiro = ''

    @observe() detalhe: Consulta[] = []
    @observe() detalhePagina = 0
    @observe() detalheTotal = 0
    @observe() carregandoDetalhe = false

    onFiltrosConsultasChange = Scope.ASYNC_ACTION_ONE<{ filtros: FiltrosConsultas; nomes?: Record<string, string> }>()
    onAlternarParceiro = Scope.ASYNC_ACTION_ONE<string[]>()
    onDetalhePaginaChange = Scope.ASYNC_ACTION_ONE<number>()
    onBuscarSugestaoParceiro = Scope.ASYNC_ACTION_STRING
}
